import axios, { AxiosInstance, AxiosError } from "axios";
import {
    LoginResponse,
    Product,
    ProductsResponse,
    Category,
    CategoriesResponse,
    CreateCategoryRequest,
    UpdateCategoryRequest,
    CreateProductRequest,
    UpdateProductRequest,
} from "../types";

const API_URL = process.env.REACT_APP_API_URL || "";

// Créer l'instance Axios
const api: AxiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

// Intercepteur pour ajouter le token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        if (error.response?.status === 401) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            window.location.href = "/admin/login";
        }
        return Promise.reject(error);
    },
);

// ============ AUTH ADMIN ============
export const adminAuthService = {
    login: async (
        username: string,
        password: string,
    ): Promise<LoginResponse> => {
        const response = await api.post<LoginResponse>("/api/v2/admin/login", {
            username,
            password,
        });
        return response.data;
    },

    logout: async (): Promise<void> => {
        await api.post("/api/v2/admin/logout");
    },
};

// ============ PRODUCTS (PUBLIC) ============
export const productService = {
    getAll: async (): Promise<Product[]> => {
        const response = await api.get<ProductsResponse>("/api/v1/products");
        return response.data.products || [];
    },

    getById: async (id: number): Promise<Product> => {
        const response = await api.get<Product>(`/api/v1/products/${id}`);
        return response.data;
    },

    getByCategory: async (categoryId: number): Promise<Product[]> => {
        const response = await api.get<ProductsResponse>(
            `/api/v1/categories/${categoryId}/products`,
        );
        return response.data.products || [];
    },
};

// ============ CATEGORIES (PUBLIC) ============
export const categoryService = {
    getAll: async (): Promise<Category[]> => {
        const response =
            await api.get<CategoriesResponse>("/api/v1/categories");
        return response.data.categories || [];
    },

    getById: async (id: number): Promise<Category> => {
        const response = await api.get<Category>(`/api/v1/categories/${id}`);
        return response.data;
    },
};

// ============ ADMIN - CATEGORIES ============
export const adminCategoryService = {
    getAll: async (): Promise<Category[]> => {
        const response = await api.get<CategoriesResponse>(
            "/api/v2/admin/categories",
        );
        return response.data.categories || [];
    },

    create: async (data: CreateCategoryRequest): Promise<Category> => {
        const response = await api.post<Category>(
            "/api/v2/admin/categories",
            data,
        );
        return response.data;
    },

    update: async (id: number, data: UpdateCategoryRequest): Promise<void> => {
        await api.put(`/api/v2/admin/categories/${id}`, data);
    },

    delete: async (id: number): Promise<void> => {
        await api.delete(`/api/v2/admin/categories/${id}`);
    },

    reorder: async (categoryIds: number[]): Promise<void> => {
        await api.put("/api/v2/admin/categories/reorder", {
            category_ids: categoryIds,
        });
    },
};

// ============ ADMIN - PRODUCTS ============
export const adminProductService = {
    getAll: async (): Promise<Product[]> => {
        const response = await api.get<ProductsResponse>(
            "/api/v2/admin/products",
        );
        return response.data.products || [];
    },

    getById: async (id: number): Promise<Product> => {
        const response = await api.get<Product>(`/api/v2/admin/products/${id}`);
        return response.data;
    },

    create: async (data: CreateProductRequest): Promise<Product> => {
        const response = await api.post<Product>(
            "/api/v2/admin/products",
            data,
        );
        return response.data;
    },

    update: async (id: number, data: UpdateProductRequest): Promise<void> => {
        await api.put(`/api/v2/admin/products/${id}`, data);
    },

    delete: async (id: number): Promise<void> => {
        await api.delete(`/api/v2/admin/products/${id}`);
    },

    addMedia: async (productId: number, file: File): Promise<void> => {
        const formData = new FormData();
        formData.append("file", file);
        await api.post(`/api/v2/admin/products/${productId}/media`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },

    deleteMedia: async (productId: number, mediaId: number): Promise<void> => {
        await api.delete(
            `/api/v2/admin/products/${productId}/media/${mediaId}`,
        );
    },

    setPrimaryMedia: async (
        productId: number,
        mediaId: number,
    ): Promise<void> => {
        await api.put(
            `/api/v2/admin/products/${productId}/media/${mediaId}/primary`,
        );
    },

    reorder: async (categoryId: number, productIds: number[]): Promise<void> => {
        await api.put("/api/v2/admin/products/reorder", {
            category_id: categoryId,
            product_ids: productIds,
        });
    },
};

export default api;
